import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SummaryTile from "./utils/SummaryTile";
import { labelWeeks } from "./utils/planUtils";
import { getCoachingForWeek } from "./utils/coachingNotes";
import {
  daysPerWeekFromPlan,
  peakLongRunMilesFromPlan,
} from "./utils/planSummaryStats";
import { weekDisplayTotals } from "./utils/planSwitcher";
import html2canvas from "html2canvas";
import pacewerksLogo from "./assets/pacewerks-logo.png";
import {
  exportPlanElementToPdfBlob,
} from "./utils/exportPlanPdf";
import {
  readSavedPlan,
  readWorkoutChecks,
  saveWorkoutChecks,
} from "./utils/savedPlanStorage";
import "./TrainingPlanOutput.css";

function formatShareRaceDate(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function buildSharePlanFilename(raceDistance) {
  const slug = String(raceDistance || "Plan").trim().replace(/\s+/g, "-");
  return `PaceWerks-${slug}-Plan.png`;
}

function daysUntilRaceLabel(isoDate) {
  if (!isoDate) return "—";
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) return "—";
  const race = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  race.setHours(0, 0, 0, 0);
  const diffDays = Math.round((race - today) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return "0 days";
  if (diffDays === 1) return "1 day";
  return `${diffDays} days`;
}

function shareHeadlineForDistance(distance) {
  switch (distance) {
    case "Marathon":
      return "Marathon mode: activated";
    case "Half Marathon":
      return "Half marathon mode: activated";
    case "10K":
      return "10K season starts now";
    case "10 Miler":
      return "10 miler mode: activated";
    case "5K":
      return "5K on the calendar";
    default:
      return "Training mode: activated";
  }
}

function shareGhostWatermark(distance) {
  switch (distance) {
    case "Marathon":
      return "26.2";
    case "Half Marathon":
      return "13.1";
    case "10K":
      return "10K";
    case "10 Miler":
      return "10";
    case "5K":
      return "5K";
    default:
      return "";
  }
}

const weekVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

/** Splits "Easy Run: description…" into label + body for scannable guidance. */
function splitGuidanceLine(text) {
  const s = String(text || "");
  const idx = s.indexOf(": ");
  if (idx <= 0) return { label: null, description: s };
  return {
    label: s.slice(0, idx).trim(),
    description: s.slice(idx + 2).trim(),
  };
}

const MI_PER_KM = 0.621371;

/** Monday–Sunday range for plan week N (1-based). e.g. "Jun 9 - 15" or "Jun 9 - Jul 15". */
function formatWeekDateRange(startDate, weekNum) {
  if (!startDate || weekNum == null) return "";
  const [year, month, day] = String(startDate).split("-").map(Number);
  if (!year || !month || !day) return "";
  const planStart = new Date(year, month - 1, day);
  const weekStart = new Date(planStart);
  weekStart.setDate(planStart.getDate() + (Number(weekNum) - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const monthFmt = new Intl.DateTimeFormat("en-US", { month: "short" });
  const dayFmt = new Intl.DateTimeFormat("en-US", { day: "numeric" });
  const startMonth = monthFmt.format(weekStart);
  const endMonth = monthFmt.format(weekEnd);
  const startDay = dayFmt.format(weekStart);
  const endDay = dayFmt.format(weekEnd);
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

function getWeekCalendarBounds(startDate, weekNum) {
  if (!startDate || weekNum == null) return null;
  const [year, month, day] = String(startDate).split("-").map(Number);
  if (!year || !month || !day) return null;
  const planStart = new Date(year, month - 1, day);
  const weekStart = new Date(planStart);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(planStart.getDate() + (Number(weekNum) - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setHours(0, 0, 0, 0);
  weekEnd.setDate(weekStart.getDate() + 6);
  return { weekStart, weekEnd };
}

/** Index of the plan week that contains today (by startDate + week number). */
export function getCurrentWeekIndex(labeledPlan, startDate) {
  if (!labeledPlan?.length) return 0;
  if (!startDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const firstWeekNum = labeledPlan[0].week ?? 1;
  const firstBounds = getWeekCalendarBounds(startDate, firstWeekNum);
  if (firstBounds && today < firstBounds.weekStart) return 0;

  const lastIndex = labeledPlan.length - 1;
  const lastWeekNum = labeledPlan[lastIndex].week ?? lastIndex + 1;
  const lastBounds = getWeekCalendarBounds(startDate, lastWeekNum);
  if (lastBounds && today > lastBounds.weekEnd) return lastIndex;

  for (let i = 0; i < labeledPlan.length; i++) {
    const weekNum = labeledPlan[i].week ?? i + 1;
    const bounds = getWeekCalendarBounds(startDate, weekNum);
    if (
      bounds &&
      today >= bounds.weekStart &&
      today <= bounds.weekEnd
    ) {
      return i;
    }
  }

  return lastIndex;
}

/** Easy / tempo / long / Saturday lines that get a warm-up · cool-down hint (display only). */
function isWarmupNoteWorkoutType(text) {
  return /^(Easy Run|Tempo Run|Long Run|Saturday Run)\b/i.test(
    String(text || "").trim()
  );
}

/**
 * First distance in the workout string (plan lines use "Type (N mi)" or "(N km / …)").
 * Returns distance in miles, or null if not parsed.
 */
function parseWorkoutDistanceMiles(text) {
  const m = String(text).match(/\(\s*(\d+(?:\.\d+)?)\s*(km|mi)\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n) || n < 0) return null;
  const unit = m[2].toLowerCase();
  return unit === "km" ? n * MI_PER_KM : n;
}

/** ≤3 mi → 5 min; >3 mi (including 4+) → 10 min — display only, from scheduled distance. */
function warmupCooldownNoteForWorkout(text) {
  if (!isWarmupNoteWorkoutType(text)) return null;
  const miles = parseWorkoutDistanceMiles(text);
  if (miles == null) return null;
  return miles <= 3
    ? "5 min warm-up / cool-down"
    : "10 min warm-up / cool-down";
}

/** Schedule lines like `Intervals (5 mi total)`. */
function isIntervalWorkoutLine(text) {
  return /^Intervals\b/i.test(String(text || "").trim());
}

/**
 * Total session distance in miles from an Intervals line, or null.
 */
function parseIntervalTotalMiles(text) {
  const m = String(text).match(
    /^Intervals\s*\(\s*(\d+(?:\.\d+)?)\s*(km|mi)\b/i
  );
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n) || n < 0) return null;
  const unit = m[2].toLowerCase();
  return unit === "km" ? n * MI_PER_KM : n;
}

const INTERVAL_RX_MODERATE = {
  2: "Warm up · 4x200m · Cool down",
  3: "Warm up · 6x200m · Cool down",
  4: "Warm up · 4x400m · Cool down",
  5: "Warm up · 6x400m · Cool down",
  6: "Warm up · 4x800m · Cool down",
};

const INTERVAL_RX_CHALLENGING = {
  2: "Warm up · 2x400m · Cool down",
  3: "Warm up · 4x400m · Cool down",
  4: "Warm up · 4x800m · Cool down",
  5: "Warm up · 5x800m · Cool down",
  6: "Warm up · 3x1600m · Cool down",
};

/** 10 Miler — Moderate (2–8 mi session). */
const INTERVAL_RX_10MILER_MODERATE = {
  2: "Warm up · 4x200m · Cool down",
  3: "Warm up · 4x400m · Cool down",
  4: "Warm up · 4x400m · Cool down",
  5: "Warm up · 4x800m · Cool down",
  6: "Warm up · 5x800m · Cool down",
  7: "Warm up · 3x1600m · Cool down",
  8: "Warm up · 4x1600m · Cool down",
};

/** 10 Miler — Challenging / Intense (2–8 mi session). */
const INTERVAL_RX_10MILER_CHALLENGING = {
  2: "Warm up · 2x400m · Cool down",
  3: "Warm up · 4x400m · Cool down",
  4: "Warm up · 4x800m · Cool down",
  5: "Warm up · 5x800m · Cool down",
  6: "Warm up · 3x1600m · Cool down",
  7: "Warm up · 4x1600m · Cool down",
  8: "Warm up · 5x1600m · Cool down",
};

/** Half Marathon — Moderate (2–8 mi session). */
const INTERVAL_RX_HALF_MARATHON_MODERATE = {
  2: "Warm up · 4x200m · Cool down",
  3: "Warm up · 4x400m · Cool down",
  4: "Warm up · 4x800m · Cool down",
  5: "Warm up · 5x800m · Cool down",
  6: "Warm up · 3x1600m · Cool down",
  7: "Warm up · 4x1600m · Cool down",
  8: "Warm up · 5x1600m · Cool down",
};

/** Half Marathon — Challenging / Intense: same reps as 10 Miler hard table. */
const INTERVAL_RX_HALF_MARATHON_CHALLENGING = INTERVAL_RX_10MILER_CHALLENGING;

/** Marathon — Moderate: same reps as Half Marathon moderate table. */
const INTERVAL_RX_MARATHON_MODERATE = INTERVAL_RX_HALF_MARATHON_MODERATE;

/** Marathon — Challenging / Intense: same reps as Half / 10 Miler hard table. */
const INTERVAL_RX_MARATHON_CHALLENGING = INTERVAL_RX_10MILER_CHALLENGING;

/**
 * Easy plans for these distances omit interval workouts — skip prescription if a line ever appears.
 */
const EASY_DISTANCE_SKIP_INTERVAL_RX = new Set([
  "5K",
  "10K",
  "10 Miler",
  "Half Marathon",
  "Marathon",
]);

/**
 * Sub-line copy for Intervals (…) — display only.
 * 10 Miler, Half Marathon, Marathon: 2–8 mi tables (Moderate vs Challenging/Intense).
 * 5K/10K: shared 2–6 mi tables by intensity.
 */
function intervalPrescriptionHint(text, intensity, raceDistance) {
  if (!isIntervalWorkoutLine(text)) return null;
  if (EASY_DISTANCE_SKIP_INTERVAL_RX.has(raceDistance) && intensity === "Easy") {
    return null;
  }
  const mi = parseIntervalTotalMiles(text);
  if (mi == null) return null;
  const bucket = Math.round(mi);

  if (raceDistance === "10 Miler") {
    if (bucket < 2 || bucket > 8) return null;
    const hard =
      intensity === "Challenging" || intensity === "Intense";
    const table = hard
      ? INTERVAL_RX_10MILER_CHALLENGING
      : INTERVAL_RX_10MILER_MODERATE;
    return table[bucket] ?? null;
  }

  if (raceDistance === "Half Marathon") {
    if (bucket < 2 || bucket > 8) return null;
    const hard =
      intensity === "Challenging" || intensity === "Intense";
    const table = hard
      ? INTERVAL_RX_HALF_MARATHON_CHALLENGING
      : INTERVAL_RX_HALF_MARATHON_MODERATE;
    return table[bucket] ?? null;
  }

  if (raceDistance === "Marathon") {
    if (bucket < 2 || bucket > 8) return null;
    const hard =
      intensity === "Challenging" || intensity === "Intense";
    const table = hard
      ? INTERVAL_RX_MARATHON_CHALLENGING
      : INTERVAL_RX_MARATHON_MODERATE;
    return table[bucket] ?? null;
  }

  if (bucket < 2 || bucket > 6) return null;
  const hard =
    intensity === "Challenging" || intensity === "Intense";
  const table = hard ? INTERVAL_RX_CHALLENGING : INTERVAL_RX_MODERATE;
  return table[bucket] ?? null;
}

/** Maps workout copy to a CSS modifier for line color. */
function workoutCheckKey(weekNum, day) {
  return `week-${weekNum}-${day}`;
}

function isRestWorkout(workout) {
  const s = String(workout || "").toLowerCase();
  if (s.includes("shake-out") || s.includes("shakeout")) return false;
  if (s.includes("cross training")) return false;
  return /\brest\b/.test(s) || /\boff\b/.test(s);
}

function workoutTextFromScheduleEntry(workout) {
  if (workout == null) return "";
  return typeof workout === "string"
    ? workout
    : JSON.stringify(workout ?? "");
}

function isRaceDayWorkout(workout) {
  const s = String(workoutTextFromScheduleEntry(workout)).toLowerCase();
  return s.includes("race day");
}

/** True when every non-rest scheduled day is checked in workoutChecks. */
export function isWeekComplete(week, workoutChecks, weekNum) {
  const schedule = week?.schedule || {};
  let hasNonRest = false;
  for (const [day, workout] of Object.entries(schedule)) {
    const text = workoutTextFromScheduleEntry(workout).trim();
    if (!text) continue;
    if (isRestWorkout(text)) continue;
    hasNonRest = true;
    if (!workoutChecks[workoutCheckKey(weekNum, day)]) return false;
  }
  return hasNonRest;
}

function isRaceDayChecked(week, workoutChecks, weekNum) {
  const schedule = week?.schedule || {};
  for (const [day, workout] of Object.entries(schedule)) {
    const text = workoutTextFromScheduleEntry(workout);
    if (isRaceDayWorkout(text)) {
      return !!workoutChecks[workoutCheckKey(weekNum, day)];
    }
  }
  return false;
}

function isShakeoutWorkout(workout) {
  const s = String(workoutTextFromScheduleEntry(workout)).toLowerCase();
  return s.includes("shake");
}

function isShakeoutChecked(week, workoutChecks, weekNum) {
  const schedule = week?.schedule || {};
  for (const [day, workout] of Object.entries(schedule)) {
    if (isShakeoutWorkout(workout)) {
      return !!workoutChecks[workoutCheckKey(weekNum, day)];
    }
  }
  return false;
}

const RACE_WEEK_TRAINING_COMPLETE_MESSAGE =
  "Training complete. Now go run your race. You've got this.";

function weekCompleteMessage(focus) {
  const f = String(focus ?? "").toLowerCase();
  if (f.includes("onboarding"))
    return "Week 1 done. You showed up. That's everything.";
  if (f.includes("build")) return "You crushed this week. Fitness is happening.";
  if (f.includes("cutback"))
    return "Smart runners respect cutback weeks. Well done.";
  if (f.includes("peak"))
    return "Peak week complete. That's the hardest week of your plan.";
  if (f.includes("taper"))
    return "Taper week done. Your legs are loading up for race day.";
  if (f.includes("race week"))
    return "Training complete. Now go run your race. You've got this.";
  return "Week complete. Keep going.";
}

function FullScreenConfetti({ onComplete }) {
  useEffect(() => {
    const t = setTimeout(onComplete, 3000);
    return () => clearTimeout(t);
  }, [onComplete]);

  const pieces = Array.from({ length: 150 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.5,
    duration: 1.2 + Math.random() * 0.8,
    color: [
      "#10B981",
      "#ffffff",
      "#a78bfa",
      "#f59e0b",
      "#3b9eff",
      "#f43f5e",
    ][Math.floor(Math.random() * 6)],
    size: 6 + Math.floor(Math.random() * 8),
    rotation: Math.random() * 360,
    tx: (Math.random() - 0.5) * 200,
  }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      {pieces.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: "-20px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            borderRadius: p.size > 10 ? "2px" : "50%",
            animation: `pw-confetti-fall ${p.duration}s ${p.delay}s ease-in forwards`,
            transform: `rotate(${p.rotation}deg)`,
            "--tx": `${p.tx}px`,
          }}
        />
      ))}
    </div>
  );
}

function modalWorkoutClassName(workout) {
  return workoutLineClassName(workout).replace(
    /^pw-workout-line/,
    "pw-week-modal-workout"
  );
}

function quickCoachingHint(workoutText) {
  const s = String(workoutText || "").toLowerCase();
  if (s.includes("race day"))
    return "This is what all the training was for. Run your race.";
  if (s.includes("shake-out") || s.includes("shakeout"))
    return "Easy and loose. Just wake the legs up.";
  if (s.includes("cross training"))
    return "Low impact, active recovery. Keep it easy and enjoy it.";
  if (s.includes("interval") || s.includes("speed"))
    return "Hard effort, short bursts. This is where speed is made.";
  if (s.includes("tempo"))
    return "Comfortably hard. You should be able to talk in short sentences.";
  if (s.includes("long run") || s.includes("saturday run"))
    return "Start easy, stay easy. Time on feet is the goal.";
  if (s.includes("easy run"))
    return "Conversational pace. Slower than you think is probably right.";
  if (s.includes("walk"))
    return "Active recovery. Keep it gentle and enjoy the movement.";
  return null;
}

function WeekExpandModal({
  week,
  weekIndex,
  weekDateRange,
  totalMileageLabel,
  intensity,
  raceDistance,
  workoutChecks,
  onToggleWorkoutCheck,
  onClose,
  closing,
  highlightDay,
}) {
  const weekNum = week?.week ?? weekIndex + 1;
  const title = `Week ${weekNum}: ${week?.focus ?? ""}`;
  const touchStartYRef = useRef(null);
  const highlightDayRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!highlightDay || closing) return;
    const node = highlightDayRef.current;
    if (!node) return;
    const frame = requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlightDay, weekIndex, closing]);

  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (e) => {
    const startY = touchStartYRef.current;
    const endY = e.changedTouches[0]?.clientY;
    touchStartYRef.current = null;
    if (startY == null || endY == null) return;
    if (endY - startY > 72) onClose();
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={
        closing
          ? "pw-week-modal-overlay pw-week-modal-overlay--closing"
          : "pw-week-modal-overlay"
      }
      role="presentation"
      onClick={onClose}
    >
      <div
        className={
          closing
            ? "pw-week-modal-panel pw-week-modal-panel--closing"
            : "pw-week-modal-panel"
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-week-modal-title"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <header className="pw-week-modal-header">
          <div className="pw-week-modal-header-text">
            <h2 id="pw-week-modal-title" className="pw-week-modal-title">
              {title}
            </h2>
            {weekDateRange ? (
              <p className="pw-week-modal-meta">{weekDateRange}</p>
            ) : null}
            {totalMileageLabel ? (
              <p className="pw-week-modal-meta">
                <strong>Total:</strong> {totalMileageLabel}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="pw-week-modal-close"
            aria-label="Close week details"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="pw-week-modal-days">
          {CALENDAR_DAY_ORDER.map((dayName) => {
            const workout = week?.schedule?.[dayName];
            const hasWorkout =
              workout != null && String(workout).trim() !== "";
            const text = !hasWorkout
              ? "Rest"
              : typeof workout === "string"
                ? workout
                : JSON.stringify(workout);
            const isRest = !hasWorkout || isRestWorkout(text);
            const mod = isRest
              ? "pw-week-modal-workout--rest"
              : modalWorkoutClassName(text);
            const { typeLine, detailLine } = hasWorkout
              ? splitWorkoutForCalendar(text)
              : { typeLine: "Rest", detailLine: "" };
            const warmupNote = hasWorkout
              ? warmupCooldownNoteForWorkout(text)
              : null;
            const intervalNote = hasWorkout
              ? intervalPrescriptionHint(text, intensity, raceDistance)
              : null;
            const quickHint = isRest ? null : quickCoachingHint(text);
            const checkKey = workoutCheckKey(weekNum, dayName);
            const isChecked = !!workoutChecks[checkKey];
            const showCheck = hasWorkout && !isRest;

            const isHighlighted = highlightDay === dayName;

            return (
              <div
                key={dayName}
                ref={isHighlighted ? highlightDayRef : undefined}
                className={[
                  "pw-week-modal-day",
                  isRest && "pw-week-modal-day--rest",
                  isHighlighted && "pw-week-modal-day--highlighted",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="pw-week-modal-day-row">
                  {showCheck ? (
                    <button
                      type="button"
                      className={`pw-workout-check pw-workout-check--modal${
                        isChecked ? " pw-workout-check--checked" : ""
                      }`}
                      aria-pressed={isChecked}
                      aria-label={
                        isChecked
                          ? `Mark ${dayName} workout incomplete`
                          : `Mark ${dayName} workout complete`
                      }
                      onClick={() => onToggleWorkoutCheck(weekNum, dayName, text)}
                    >
                      {isChecked ? <WorkoutCheckIcon /> : null}
                    </button>
                  ) : (
                    <span className="pw-week-modal-check-spacer" aria-hidden />
                  )}
                  <div className="pw-week-modal-day-body">
                    <div className="pw-week-modal-day-name">
                      {CALENDAR_DAY_SHORT[dayName] ?? dayName.slice(0, 3)}
                    </div>
                    <div className={`pw-week-modal-workout ${mod}`}>
                      {typeLine || text}
                    </div>
                    {detailLine ? (
                      <div className="pw-week-modal-mileage">{detailLine}</div>
                    ) : null}
                    {warmupNote ? (
                      <p className="pw-week-modal-note">{warmupNote}</p>
                    ) : null}
                    {intervalNote ? (
                      <p className="pw-week-modal-note pw-week-modal-note--interval">
                        {intervalNote}
                      </p>
                    ) : null}
                    {quickHint ? (
                      <p className="pw-week-modal-quick-hint">{quickHint}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

function WorkoutCheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden>
      <path
        d="M3 8.5 L6.5 12 L13 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function workoutLineClassName(workout) {
  const s = String(workout || "").toLowerCase();
  if (s.includes("race day")) return "pw-workout-line--raceday";
  if (s.includes("shake-out") || s.includes("shakeout"))
    return "pw-workout-line--shakeout";
  if (/\brest\b/.test(s) || /\boff\b/.test(s)) return "pw-workout-line--rest";
  if (s.includes("cross training")) return "pw-workout-line--xtraining";
  if (s.includes("tempo")) return "pw-workout-line--tempo";
  if (s.includes("interval")) return "pw-workout-line--interval";
  if (s.includes("long run") || s.includes("saturday run"))
    return "pw-workout-line--longrun";
  return "pw-workout-line--easy";
}

const CALENDAR_DAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const CALENDAR_DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

/** Calendar cell modifier — same tokens as list view (`pw-plan-cal-cell--tempo`, etc.). */
function calendarCellClassName(workout) {
  return workoutLineClassName(workout).replace(
    /^pw-workout-line/,
    "pw-plan-cal-cell"
  );
}

/** Split "Easy Run (3 mi)" → type line + mileage/detail line for the grid. */
function splitWorkoutForCalendar(workout) {
  const raw =
    typeof workout === "string" ? workout : JSON.stringify(workout ?? "");
  const s = raw.trim();
  const open = s.indexOf("(");
  if (open > 0) {
    return {
      typeLine: s.slice(0, open).trim(),
      detailLine: s.slice(open).trim(),
    };
  }
  return { typeLine: s, detailLine: "" };
}

/** Shared clock icon for warm-up / cool-down hints (list + calendar). */
function WarmupHintClockIcon({ size = 13 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="8.5"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M12 7.5v4.25l2.75 1.75"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Small runner icon for interval prescription hints (list + calendar). */
function IntervalHintRunIcon({ size = 11 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="9" cy="6.5" r="2" fill="currentColor" />
      <path
        d="M7 21l2.5-6 2 1 3 5M9.5 15l4-2 3.5 1.5M6 12l3-2.5 2.5 1"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PlanWeekCalendarGrid({
  schedule,
  intensity,
  raceDistance,
  weekNum = 1,
  workoutChecks = {},
  onToggleWorkoutCheck = () => {},
}) {
  return (
    <div className="pw-cal-scroll-container">
      <div className="pw-plan-cal-grid-wrap">
        <div className="pw-plan-cal-grid" aria-label="Week schedule">
          {CALENDAR_DAY_ORDER.map((dayName) => {
            const workout = schedule?.[dayName];
            const hasWorkout =
              workout != null && String(workout).trim() !== "";
            const text = !hasWorkout
              ? ""
              : typeof workout === "string"
                ? workout
                : JSON.stringify(workout);
            const mod = !hasWorkout
              ? "pw-plan-cal-cell--empty"
              : calendarCellClassName(text);
            const { typeLine, detailLine } = hasWorkout
              ? splitWorkoutForCalendar(text)
              : { typeLine: "", detailLine: "" };
            const displayType = !hasWorkout ? "—" : typeLine || "—";
            const warmupNote = hasWorkout
              ? warmupCooldownNoteForWorkout(text)
              : null;
            const intervalNote = hasWorkout
              ? intervalPrescriptionHint(text, intensity, raceDistance)
              : null;
            const isRest = hasWorkout && isRestWorkout(text);
            const checkKey = workoutCheckKey(weekNum, dayName);
            const isChecked = !!workoutChecks[checkKey];
            const showCheck = hasWorkout && !isRest;
            return (
              <div
                key={dayName}
                className={`pw-plan-cal-cell ${mod}${
                  isChecked ? " pw-plan-cal-cell--completed" : ""
                }`}
              >
                {showCheck ? (
                  <button
                    type="button"
                    className={`pw-workout-check pw-workout-check--calendar${
                      isChecked ? " pw-workout-check--checked" : ""
                    }`}
                    aria-pressed={isChecked}
                    aria-label={
                      isChecked
                        ? `Mark ${dayName} workout incomplete`
                        : `Mark ${dayName} workout complete`
                    }
                    onClick={() => onToggleWorkoutCheck(weekNum, dayName, text)}
                  >
                    {isChecked ? <WorkoutCheckIcon /> : null}
                  </button>
                ) : null}
                <div className="pw-plan-cal-day">
                  {CALENDAR_DAY_SHORT[dayName] ?? dayName.slice(0, 3)}
                </div>
                <div className="pw-plan-cal-type">{displayType}</div>
                {(detailLine || warmupNote || intervalNote) ? (
                  <div className="pw-plan-cal-lower">
                    {detailLine ? (
                      <div className="pw-plan-cal-detail">{detailLine}</div>
                    ) : null}
                    {warmupNote ? (
                      <div className="pw-plan-cal-warmup-note" role="note">
                        <span
                          className="pw-plan-cal-warmup-note-icon"
                          aria-hidden="true"
                        >
                          <WarmupHintClockIcon size={11} />
                        </span>
                        <span>{warmupNote}</span>
                      </div>
                    ) : null}
                    {intervalNote ? (
                      <div className="pw-plan-cal-warmup-note" role="note">
                        <span
                          className="pw-plan-cal-warmup-note-icon"
                          aria-hidden="true"
                        >
                          <IntervalHintRunIcon size={11} />
                        </span>
                        <span>{intervalNote}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="pw-cal-fade-hint" aria-hidden="true" />
      </div>
      <p className="pw-cal-swipe-hint" data-pw-exclude-pdf>
        ← Swipe to see full week →
      </p>
    </div>
  );
}

function localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildPlanDaysByDate(labeledPlan, startDate) {
  const map = new Map();
  if (!startDate || !labeledPlan?.length) return map;

  labeledPlan.forEach((week, weekIndex) => {
    const weekNum = week.week ?? weekIndex + 1;
    const bounds = getWeekCalendarBounds(startDate, weekNum);
    if (!bounds) return;

    CALENDAR_DAY_ORDER.forEach((dayName, dayOffset) => {
      const cellDate = new Date(bounds.weekStart);
      cellDate.setDate(bounds.weekStart.getDate() + dayOffset);
      cellDate.setHours(0, 0, 0, 0);

      const workout = week.schedule?.[dayName];
      const text = workoutTextFromScheduleEntry(workout).trim();
      if (!text) return;

      map.set(localDateKey(cellDate), {
        weekIndex,
        weekNum,
        dayName,
        text,
      });
    });
  });

  return map;
}

function getPlanDateBounds(labeledPlan, startDate) {
  if (!startDate || !labeledPlan?.length) return null;
  const firstNum = labeledPlan[0].week ?? 1;
  const lastIndex = labeledPlan.length - 1;
  const lastNum = labeledPlan[lastIndex].week ?? lastIndex + 1;
  const first = getWeekCalendarBounds(startDate, firstNum);
  const last = getWeekCalendarBounds(startDate, lastNum);
  if (!first || !last) return null;
  return { planStart: first.weekStart, planEnd: last.weekEnd };
}

function abbreviateWorkoutForMonth(workout) {
  const s = String(workout || "").toLowerCase();
  if (s.includes("race day")) return "Race Day";
  if (s.includes("shake-out") || s.includes("shakeout")) return "Shakeout";
  if (s.includes("cross training")) return "X-Train";
  if (s.includes("intervals")) return "Speed";
  if (s.includes("tempo")) return "Tempo";
  if (s.includes("long run") || s.includes("saturday run")) return "Long Run";
  if (s.includes("easy run")) return "Easy Run";
  if (s.includes("walk")) return "Walk";
  if (/\brest\b/.test(s)) return "Rest";
  return "Run";
}

function monthDayMileageLabel(text) {
  if (isRestWorkout(text)) return null;
  const m = String(text).match(/\(\s*(\d+(?:\.\d+)?)\s*(km|mi)\b/i);
  if (!m) return null;
  const unit = m[2].toLowerCase() === "km" ? "km" : "mi";
  return `${m[1]} ${unit}`;
}

function monthDayCellModifier(text) {
  return calendarCellClassName(text).replace(
    /^pw-plan-cal-cell/,
    "pw-month-day"
  );
}

function monthDayWorkoutClass(text) {
  return workoutLineClassName(text).replace(
    /^pw-workout-line/,
    "pw-month-day-workout"
  );
}

export function PlanMonthView({
  labeledPlan,
  startDate,
  workoutChecks,
  currentWeekIndex,
  onOpenWeek,
}) {
  const planDaysByDate = useMemo(
    () => buildPlanDaysByDate(labeledPlan, startDate),
    [labeledPlan, startDate]
  );
  const planBounds = useMemo(
    () => getPlanDateBounds(labeledPlan, startDate),
    [labeledPlan, startDate]
  );

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const planIdentity = `${startDate}|${labeledPlan.length}`;
  const currentWeekNum =
    labeledPlan[currentWeekIndex]?.week ?? currentWeekIndex + 1;

  useEffect(() => {
    if (!startDate || !labeledPlan.length) return;
    const bounds = getWeekCalendarBounds(startDate, currentWeekNum);
    if (bounds) {
      setCurrentMonth(
        new Date(
          bounds.weekStart.getFullYear(),
          bounds.weekStart.getMonth(),
          1
        )
      );
    }
  }, [planIdentity, currentWeekNum, startDate, labeledPlan.length]);

  const todayKey = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return localDateKey(t);
  }, []);

  const monthYear = currentMonth.getFullYear();
  const monthIndex = currentMonth.getMonth();
  const monthLabel = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(currentMonth);

  const gridCells = useMemo(() => {
    const firstOfMonth = new Date(monthYear, monthIndex, 1);
    const startPad = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(monthYear, monthIndex + 1, 0).getDate();
    const totalCells = Math.ceil((startPad + daysInMonth) / 7) * 7;
    const cells = [];

    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startPad + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push({ kind: "pad" });
        continue;
      }

      const cellDate = new Date(monthYear, monthIndex, dayNum);
      cellDate.setHours(0, 0, 0, 0);
      const key = localDateKey(cellDate);
      const dayData = planDaysByDate.get(key);

      const inPlanRange =
        planBounds &&
        cellDate >= planBounds.planStart &&
        cellDate <= planBounds.planEnd;

      if (!inPlanRange || !dayData) {
        cells.push({ kind: "empty", dayNum, cellDate, key });
        continue;
      }

      const abbrev = abbreviateWorkoutForMonth(dayData.text);
      const miles = monthDayMileageLabel(dayData.text);
      const isRest = isRestWorkout(dayData.text);
      const isRace = isRaceDayWorkout(dayData.text);
      const checkKey = workoutCheckKey(dayData.weekNum, dayData.dayName);
      const isChecked = !!workoutChecks[checkKey];
      const isToday = key === todayKey;

      cells.push({
        kind: "plan",
        dayNum,
        cellDate,
        key,
        dayData,
        abbrev,
        miles,
        isRest,
        isRace,
        isChecked,
        isToday,
        mod: monthDayCellModifier(dayData.text),
        workoutClass: monthDayWorkoutClass(dayData.text),
      });
    }

    return cells;
  }, [
    monthYear,
    monthIndex,
    planDaysByDate,
    planBounds,
    todayKey,
    workoutChecks,
  ]);

  const goPrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  return (
    <div className="pw-month-view" aria-label="Training plan month view">
      <div className="pw-month-nav">
        <button
          type="button"
          className="pw-month-nav-btn"
          onClick={goPrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <h2 className="pw-month-nav-title">{monthLabel}</h2>
        <button
          type="button"
          className="pw-month-nav-btn"
          onClick={goNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div
        className="pw-month-grid pw-month-grid--weekdays"
        aria-hidden="true"
      >
        {CALENDAR_DAY_ORDER.map((dayName) => (
          <div key={dayName} className="pw-month-weekday">
            {CALENDAR_DAY_SHORT[dayName]}
          </div>
        ))}
      </div>
      <div className="pw-month-grid">
        {gridCells.map((cell, idx) => {
          if (cell.kind === "pad") {
            return (
              <div
                key={`pad-${idx}`}
                className="pw-month-day pw-month-day--pad"
                aria-hidden="true"
              >
                <span className="pw-month-day-number" />
              </div>
            );
          }

          if (cell.kind === "empty") {
            return (
              <div
                key={cell.key}
                className="pw-month-day pw-month-day--empty"
                aria-hidden="true"
              >
                <span className="pw-month-day-number">{cell.dayNum}</span>
              </div>
            );
          }

          const {
            dayNum,
            dayData,
            abbrev,
            miles,
            isRest,
            isRace,
            isChecked,
            isToday,
            mod,
            workoutClass,
          } = cell;

          return (
            <button
              key={cell.key}
              type="button"
              className={[
                "pw-month-day",
                mod,
                isToday && "pw-month-day--today",
                isRace && "pw-month-day--raceday",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() =>
                onOpenWeek(dayData.weekIndex, dayData.dayName)
              }
              aria-label={`${abbrev}${miles ? `, ${miles}` : ""}, open week ${dayData.weekNum}`}
            >
              <span className="pw-month-day-number">{dayNum}</span>
              {isChecked && !isRest ? (
                <span className="pw-month-day-check" aria-hidden="true">
                  <WorkoutCheckIcon />
                </span>
              ) : null}
              <span className={`pw-month-day-workout ${workoutClass}`}>
                {abbrev}
              </span>
              {miles ? (
                <span className="pw-month-day-miles">{miles}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const EMAIL_PDF_BASE64_MAX = 3 * 1024 * 1024;

const EMAIL_SUCCESS_DEFAULT = "Plan sent! Check your inbox 📬";
const EMAIL_SUCCESS_NO_ATTACHMENT =
  "Plan summary sent! Your plan is too large to attach.";

const FOCUS_MODE_STORAGE_KEY = "pw-focus-mode";
const PLAN_VIEW_MODE_KEY = "pw-plan-view-mode";

function readPlanViewMode() {
  try {
    const stored = localStorage.getItem(PLAN_VIEW_MODE_KEY);
    if (stored === "month" || stored === "calendar") return stored;
  } catch {
    /* ignore */
  }
  return "month";
}

function readFocusModePreference() {
  try {
    const stored = localStorage.getItem(FOCUS_MODE_STORAGE_KEY);
    if (stored === "0" || stored === "false") return false;
    if (stored === "1" || stored === "true") return true;
  } catch {
    /* ignore */
  }
  return true;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function scrollToEmailCapture(emailSection) {
  if (emailSection instanceof HTMLElement) {
    emailSection.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read PDF."));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("Could not read PDF."));
    reader.readAsDataURL(blob);
  });
}

export default function TrainingPlanOutput({ formData, planWeeks }) {
  const planContentRef = useRef(null);
  const shareCardRef = useRef(null);
  const emailCaptureRef = useRef(null);
  const workoutChecksRef = useRef({});
  const [shareBusy, setShareBusy] = useState(false);
  const [storedFormData, setStoredFormData] = useState(null);
  const [storedPlanWeeks, setStoredPlanWeeks] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  /** Per week index: expanded coaching (default collapsed when there is more than preview). */
  const [coachingOpenByWeek, setCoachingOpenByWeek] = useState({});
  /** Month vs week layout. Persisted; list is no longer a view. */
  const [planViewMode, setPlanViewMode] = useState(readPlanViewMode);
  /** Show coaching note panels on week cards (not persisted). */
  const [coachingNotesVisible, setCoachingNotesVisible] = useState(false);
  const [emailCapture, setEmailCapture] = useState("");
  const [emailSendState, setEmailSendState] = useState("idle"); // idle | loading | success | error
  const [emailError, setEmailError] = useState("");
  const [emailSuccessMessage, setEmailSuccessMessage] = useState(
    EMAIL_SUCCESS_DEFAULT
  );
  const [emailLoadingHint, setEmailLoadingHint] = useState("");
  const [workoutChecks, setWorkoutChecks] = useState(() => readWorkoutChecks());
  const [fullScreenConfettiRunId, setFullScreenConfettiRunId] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [weekModalClosing, setWeekModalClosing] = useState(false);
  const [focusMode, setFocusMode] = useState(() => readFocusModePreference());
  const [manuallyExpandedWeeks, setManuallyExpandedWeeks] = useState(
    () => new Set()
  );

  useEffect(() => {
    try {
      localStorage.setItem(PLAN_VIEW_MODE_KEY, planViewMode);
    } catch {
      /* ignore */
    }
  }, [planViewMode]);

  useEffect(() => {
    const saved = readSavedPlan();
    if (saved) {
      setStoredFormData(saved.formData);
      setStoredPlanWeeks(saved.planWeeks);
    } else {
      setStoredFormData(null);
      setStoredPlanWeeks([]);
    }
    workoutChecksRef.current = readWorkoutChecks();
    setHydrated(true);
  }, []);

  const effectiveFormData = formData ?? storedFormData;
  /** Parent may pass [] to mean “cleared”; omit prop (undefined) to hydrate from localStorage (e.g. App route). */
  const effectivePlanWeeks =
    Array.isArray(planWeeks) && planWeeks.length > 0
      ? planWeeks
      : Array.isArray(planWeeks) && planWeeks.length === 0
        ? []
        : storedPlanWeeks;

  const planGenerationKey = useMemo(() => {
    if (!effectiveFormData || !effectivePlanWeeks?.length) return "";
    const last = effectivePlanWeeks[effectivePlanWeeks.length - 1];
    return [
      effectiveFormData.raceDistance,
      effectiveFormData.raceDate,
      effectiveFormData.startDate,
      effectiveFormData.intensity,
      effectivePlanWeeks.length,
      effectivePlanWeeks[0]?.totalMileage,
      last?.totalMileage,
    ].join("|");
  }, [effectiveFormData, effectivePlanWeeks]);

  useEffect(() => {
    if (!planGenerationKey) return;
    setEmailCapture("");
    setEmailSendState("idle");
    setEmailError("");
    setEmailSuccessMessage(EMAIL_SUCCESS_DEFAULT);
    setEmailLoadingHint("");
    const checks = readWorkoutChecks();
    setWorkoutChecks(checks);
    workoutChecksRef.current = checks;
  }, [planGenerationKey]);

  const dismissFullScreenConfetti = useCallback(() => {
    setFullScreenConfettiRunId(null);
  }, []);

  const closeWeekModal = useCallback(() => {
    setWeekModalClosing(true);
    window.setTimeout(() => {
      setExpandedWeek(null);
      setWeekModalClosing(false);
    }, 280);
  }, []);

  const toggleFocusMode = useCallback(() => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(FOCUS_MODE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleManualWeekExpand = useCallback((weekIndex) => {
    setManuallyExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekIndex)) next.delete(weekIndex);
      else next.add(weekIndex);
      return next;
    });
  }, []);

  const toggleWorkoutCheck = useCallback((weekNum, day, workoutText) => {
    if (isRestWorkout(workoutText)) return;
    const key = workoutCheckKey(weekNum, day);
    const isCurrentlyChecked = !!workoutChecksRef.current[key];
    const isChecking = !isCurrentlyChecked;

    setWorkoutChecks((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      saveWorkoutChecks(next);
      workoutChecksRef.current = next;
      return next;
    });

    if (isChecking && isRaceDayWorkout(workoutText)) {
      setFullScreenConfettiRunId((id) => (id ?? 0) + 1);
    }
  }, []);

  if (!hydrated && !formData && !planWeeks?.length) return null;

  if (hydrated && !effectiveFormData && !effectivePlanWeeks?.length) {
    return <Navigate to="/build" replace />;
  }

  if (!effectiveFormData || !effectivePlanWeeks?.length) return null;

  // -----------------------------------------------------
  // 1️⃣ LABEL WEEKS FIRST
  // -----------------------------------------------------
  const labelData = labelWeeks({
    planWeeks: effectivePlanWeeks,
    distance: effectiveFormData.raceDistance,
  });

  // -----------------------------------------------------
  // 2️⃣ Construct the labeled + annotated plan
  // -----------------------------------------------------
  const labeledPlan = effectivePlanWeeks.map((week, idx) => {
    const entry = labelData[idx] || {};
    const suggested = typeof entry.label === "string" ? entry.label : "";

    // Keep whatever the template already defined
    const existing =
      typeof week.focus === "string" && week.focus.trim()
        ? week.focus.trim()
        : typeof week.label === "string" && week.label.trim()
        ? week.label.trim()
        : "";

    const finalLabel = existing || suggested;

    return {
      ...week,
      label: finalLabel,
      focus: finalLabel, // compatibility
      labelMeta: entry.meta || null,
      schedule: week.schedule
        ? week.schedule
        : Array.isArray(week.days)
        ? Object.fromEntries(week.days.map((d) => [d.day, d.workout]))
        : {},
    };
  });

  const isReady = labeledPlan.length > 0;
  const unitPref = effectiveFormData?.unitPreference ?? "Miles";
  const raceDist = effectiveFormData?.raceDistance ?? "";

  const weekDisplayList = labeledPlan.map((w) =>
    weekDisplayTotals(
      w,
      unitPref,
      raceDist,
      /race/i.test(String(w.focus || w.label || ""))
    )
  );
  const totalPlanKmOrMi = Math.round(
    weekDisplayList.reduce((sum, x) => sum + x.totalMileage, 0)
  );
  const week1Display = weekDisplayList[0]?.totalMileage;
  const peakWeeklyDisplay = weekDisplayList.length
    ? Math.max(...weekDisplayList.map((x) => x.totalMileage))
    : null;

  const shareDaysPerWeek = daysPerWeekFromPlan(labeledPlan);
  const shareRaceDateLabel = formatShareRaceDate(
    effectiveFormData?.raceDate ?? ""
  );
  const shareDaysUntilRace = daysUntilRaceLabel(
    effectiveFormData?.raceDate ?? ""
  );
  const unitSuffix = unitPref === "Kilometers" ? "km" : "mi";
  const shareHeadline = shareHeadlineForDistance(raceDist);
  const shareGhostLabel = shareGhostWatermark(raceDist);
  const sharePeakLongRun = peakLongRunMilesFromPlan(labeledPlan, unitPref);
  const sharePeakLongRunLabel =
    sharePeakLongRun != null && Number.isFinite(sharePeakLongRun)
      ? `${Math.round(sharePeakLongRun)} ${unitSuffix}`
      : "—";
  const shareTotalPlanLabel = `${totalPlanKmOrMi} ${unitSuffix}`;

  const handleSharePlan = useCallback(async () => {
    const el = shareCardRef.current;
    if (!el || !isReady || shareBusy) return;
    setShareBusy(true);
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 1,
        width: 1080,
        height: 1080,
        useCORS: true,
        logging: false,
      });
      const filename = buildSharePlanFilename(effectiveFormData?.raceDistance);
      const dataUrl = canvas.toDataURL("image/png");

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) =>
            b ? resolve(b) : reject(new Error("Failed to encode image")),
          "image/png"
        );
      });

      if (navigator.share && navigator.canShare) {
        const file = new File([blob], filename, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: "My PaceWerks Training Plan",
            });
            return;
          } catch (shareErr) {
            if (shareErr?.name === "AbortError") return;
            throw shareErr;
          }
        }
        window.open(dataUrl, "_blank");
        return;
      }

      const link = document.createElement("a");
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Share image export failed:", err);
      window.alert(
        "Could not create the share image. Try again in a moment."
      );
    } finally {
      setShareBusy(false);
    }
  }, [isReady, shareBusy, effectiveFormData?.raceDistance]);

  const handleSendPlanEmail = useCallback(async () => {
    const trimmed = emailCapture.trim();
    if (!trimmed || emailSendState === "loading") return;
    const el = planContentRef.current;
    if (!el || !isReady) return;
    setEmailSendState("loading");
    setEmailError("");
    setEmailLoadingHint(
      "Sending your plan, this may take just a moment..."
    );
    try {
      const planSummary = {
        raceDistance: effectiveFormData?.raceDistance ?? "",
        raceDate: effectiveFormData?.raceDate ?? "",
        intensity: effectiveFormData?.intensity ?? "",
        trainingWeeks: labeledPlan.length,
        peakWeeklyMileage:
          peakWeeklyDisplay != null && Number.isFinite(peakWeeklyDisplay)
            ? `${Math.round(peakWeeklyDisplay)} ${unitSuffix}`
            : "—",
      };

      const previousViewMode = planViewMode;
      const previousNotesVisible = coachingNotesVisible;
      const previousFocusMode = focusMode;
      const needsRenderWait =
        planViewMode !== "calendar" ||
        coachingNotesVisible ||
        previousFocusMode;

      if (planViewMode !== "calendar") {
        setPlanViewMode("calendar");
      }
      if (coachingNotesVisible) {
        setCoachingNotesVisible(false);
      }
      if (previousFocusMode) {
        setFocusMode(false);
      }
      if (needsRenderWait) {
        await delay(300);
      }

      let pdfBlob;
      let pdfBase64;

      try {
        await delay(200);

        pdfBlob = await exportPlanElementToPdfBlob(el);
        pdfBase64 = await blobToBase64(pdfBlob);

        if (pdfBase64.length > EMAIL_PDF_BASE64_MAX) {
          pdfBlob = await exportPlanElementToPdfBlob(el, {
            scale: 1.25,
            jpegQuality: 0.7,
          });
          pdfBase64 = await blobToBase64(pdfBlob);
        }
      } finally {
        if (previousViewMode !== "calendar") {
          setPlanViewMode(previousViewMode);
        }
        if (previousNotesVisible) {
          setCoachingNotesVisible(true);
        }
        if (previousFocusMode) {
          setFocusMode(previousFocusMode);
        }
      }

      scrollToEmailCapture(emailCaptureRef.current);

      const attachPdf = pdfBase64.length <= EMAIL_PDF_BASE64_MAX;

      const res = await fetch("/api/send-plan-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          ...(attachPdf ? { pdfBase64 } : {}),
          planSummary,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Could not send email.");
      }
      setEmailSuccessMessage(
        attachPdf ? EMAIL_SUCCESS_DEFAULT : EMAIL_SUCCESS_NO_ATTACHMENT
      );
      setEmailSendState("success");
    } catch (err) {
      scrollToEmailCapture(emailCaptureRef.current);
      setEmailError(err.message || "Could not send email.");
      setEmailSendState("error");
    } finally {
      scrollToEmailCapture(emailCaptureRef.current);
      setEmailLoadingHint("");
    }
  }, [
    emailCapture,
    emailSendState,
    effectiveFormData,
    isReady,
    labeledPlan.length,
    peakWeeklyDisplay,
    planViewMode,
    coachingNotesVisible,
    focusMode,
    setFocusMode,
    unitSuffix,
  ]);

  const currentWeekIndex = isReady
    ? getCurrentWeekIndex(labeledPlan, effectiveFormData?.startDate)
    : 0;

  const openWeekByIndex = useCallback(
    (weekIndex, highlightDay = null) => {
      const week = labeledPlan[weekIndex];
      if (!week) return;
      const displayWeek = weekDisplayList[weekIndex];
      const weekDateRange = formatWeekDateRange(
        effectiveFormData?.startDate,
        week.week ?? weekIndex + 1
      );
      const totalMileageLabel =
        displayWeek?.totalMileageLabel ??
        `${displayWeek?.totalMileage ?? 0} ${
          unitPref === "Kilometers" ? "km" : "mi"
        }`;
      setExpandedWeek({
        week,
        weekIndex,
        weekDateRange,
        totalMileageLabel,
        highlightDay: highlightDay || undefined,
      });
    },
    [
      labeledPlan,
      weekDisplayList,
      effectiveFormData?.startDate,
      unitPref,
    ]
  );

  // -----------------------------------------------------
  //  Render UI
  // -----------------------------------------------------
  return (
    <div
      className={`pw-plan-shell${
        planViewMode === "month" || planViewMode === "calendar"
          ? " pw-plan-shell--month-active"
          : ""
      }`}
    >
      {fullScreenConfettiRunId != null ? (
        <FullScreenConfetti
          key={fullScreenConfettiRunId}
          onComplete={dismissFullScreenConfetti}
        />
      ) : null}
      {isReady && (
        <div className="pw-plan-pdf-toolbar">
          <button
            type="button"
            className="pw-plan-share-btn"
            onClick={handleSharePlan}
            disabled={shareBusy}
          >
            {shareBusy ? "Creating image…" : "Share"}
          </button>
        </div>
      )}
      {isReady ? (
        <div className="pw-plan-share-card-wrapper" aria-hidden="true">
          <div
            ref={shareCardRef}
            className="pw-plan-share-card"
            data-pw-exclude-pdf
          >
          <img
            className="pw-plan-share-logo"
            src={pacewerksLogo}
            alt=""
            width={200}
            height={40}
          />
          <h2 className="pw-plan-share-headline">{shareHeadline}</h2>
          {shareGhostLabel ? (
            <span className="pw-plan-share-ghost" aria-hidden="true">
              {shareGhostLabel}
            </span>
          ) : null}
          <div className="pw-plan-share-stats-wrap">
            <div className="pw-plan-share-stats">
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Race date</span>
                <span className="pw-plan-share-stat-value">
                  {shareRaceDateLabel}
                </span>
              </div>
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Days until race</span>
                <span className="pw-plan-share-stat-value">
                  {shareDaysUntilRace}
                </span>
              </div>
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Training weeks</span>
                <span className="pw-plan-share-stat-value">
                  {labeledPlan.length}
                </span>
              </div>
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Days per week</span>
                <span className="pw-plan-share-stat-value">
                  {shareDaysPerWeek ?? "—"}
                </span>
              </div>
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Peak long run</span>
                <span className="pw-plan-share-stat-value">
                  {sharePeakLongRunLabel}
                </span>
              </div>
              <div className="pw-plan-share-stat">
                <span className="pw-plan-share-stat-label">Total plan miles</span>
                <span className="pw-plan-share-stat-value">
                  {shareTotalPlanLabel}
                </span>
              </div>
            </div>
          </div>
          <p className="pw-plan-share-footer">
            Free personalized training plans · pacewerks.com
          </p>
          </div>
        </div>
      ) : null}
      <div id="plan-content" ref={planContentRef}>
      {isReady && (
        <SummaryTile
          race={effectiveFormData?.raceDistance ?? ""}
          raceDate={effectiveFormData?.raceDate ?? ""}
          intensity={effectiveFormData?.intensity ?? ""}
          trainingWeeks={labeledPlan.length}
          totalPlanMileage={totalPlanKmOrMi}
          unitPreference={unitPref}
        />
      )}

      {isReady && (
        <div className="pw-plan-view-controls">
          <div
            className="pw-plan-view-toggle"
            role="group"
            aria-label="Training plan layout"
          >
            <button
              type="button"
              className={planViewMode === "month" ? "is-active" : undefined}
              aria-pressed={planViewMode === "month"}
              onClick={() => setPlanViewMode("month")}
            >
              Month
            </button>
            <button
              type="button"
              className={
                planViewMode === "calendar" ? "is-active" : undefined
              }
              aria-pressed={planViewMode === "calendar"}
              onClick={() => setPlanViewMode("calendar")}
            >
              Week
            </button>
          </div>
          {planViewMode !== "month" && (
          <div
            className="pw-plan-view-toggle"
            role="group"
            aria-label="Coaching notes on week cards"
          >
            <button
              type="button"
              className={coachingNotesVisible ? "is-active" : undefined}
              aria-pressed={coachingNotesVisible}
              onClick={() => setCoachingNotesVisible(true)}
            >
              Notes on
            </button>
            <button
              type="button"
              className={!coachingNotesVisible ? "is-active" : undefined}
              aria-pressed={!coachingNotesVisible}
              onClick={() => setCoachingNotesVisible(false)}
            >
              Notes off
            </button>
          </div>
          )}
          {planViewMode !== "month" && (
          <div
            className="pw-plan-view-toggle"
            role="group"
            aria-label="Focus mode"
          >
            <button
              type="button"
              className={focusMode ? "is-active pw-focus-mode-toggle" : undefined}
              aria-pressed={focusMode}
              onClick={toggleFocusMode}
            >
              Focus mode
            </button>
          </div>
          )}
        </div>
      )}

      {isReady ? (
        <div key={planViewMode} className="pw-plan-view-content">
        {planViewMode === "month" ? (
          <PlanMonthView
            labeledPlan={labeledPlan}
            startDate={effectiveFormData?.startDate ?? ""}
            workoutChecks={workoutChecks}
            currentWeekIndex={currentWeekIndex}
            onOpenWeek={openWeekByIndex}
          />
        ) : (
        labeledPlan.map((week, i) => {
          const displayWeek = weekDisplayList[i];
          const focusText = String(week.focus || "").toLowerCase();
          const isRaceWeek = focusText.includes("race");
          const isCurrentWeek = i === currentWeekIndex;
          const isPastWeek = i < currentWeekIndex;
          const totalMileageLabel =
            displayWeek?.totalMileageLabel ??
            `${displayWeek?.totalMileage ?? 0} ${
              unitPref === "Kilometers" ? "km" : "mi"
            }`;

          const weekDateRange = formatWeekDateRange(
            effectiveFormData?.startDate,
            week.week ?? i + 1
          );

          const coaching = getCoachingForWeek({
            weekIndex: i,
            labeledPlan,
            raceDistance: raceDist,
            intensity: effectiveFormData?.intensity,
          });

          const coachingExpanded = !!coachingOpenByWeek[i];
          const bodyParas = coaching?.bodyParagraphs ?? [];
          const hasMoreCoaching =
            !!coaching &&
            (bodyParas.length > 1 ||
              (coaching.guidance && coaching.guidance.length > 0) ||
              !!coaching.raceDay ||
              !!coaching.recovery);
          const isCalendarView = planViewMode === "calendar";
          const showCollapsedPreview =
            coaching && hasMoreCoaching && !coachingExpanded;

          const setCoachingWeekOpen = (open) => {
            setCoachingOpenByWeek((prev) => ({ ...prev, [i]: open }));
          };

          const weekNum = week.week ?? i + 1;
          const weekComplete = isWeekComplete(
            week,
            workoutChecks,
            weekNum
          );
          const raceDayChecked = isRaceDayChecked(
            week,
            workoutChecks,
            weekNum
          );
          const shakeoutChecked = isShakeoutChecked(
            week,
            workoutChecks,
            weekNum
          );
          const showWeekCompleteMessage = isRaceWeek
            ? shakeoutChecked && !raceDayChecked
            : weekComplete;
          const weekCompleteMessageText = isRaceWeek
            ? RACE_WEEK_TRAINING_COMPLETE_MESSAGE
            : weekCompleteMessage(week.focus);

          const isFocusCollapsed =
            focusMode &&
            !isCurrentWeek &&
            !manuallyExpandedWeeks.has(i);

          const weekCardClass = [
            "pw-plan-week-card",
            weekComplete && "pw-plan-week-card--complete",
            isFocusCollapsed && "pw-plan-week-card--collapsed",
            isFocusCollapsed && isPastWeek && "pw-plan-week-card--past",
          ]
            .filter(Boolean)
            .join(" ");

          const openWeekExpandModal = () => {
            setExpandedWeek({
              week,
              weekIndex: i,
              weekDateRange,
              totalMileageLabel,
            });
          };

          const handleWeekHeaderClick = (e) => {
            if (focusMode && !isCurrentWeek) {
              e.stopPropagation();
              toggleManualWeekExpand(i);
              return;
            }
            openWeekExpandModal();
          };

          return (
            <motion.div
              key={week.week ?? i}
              variants={weekVariants}
              initial="hidden"
              animate="visible"
              className={weekCardClass}
              data-pw-plan-section="week"
              data-pw-race-week={isRaceWeek ? "" : undefined}
              onClick={
                isFocusCollapsed
                  ? () => toggleManualWeekExpand(i)
                  : undefined
              }
            >
              <div className="pw-plan-week-header">
                <button
                  type="button"
                  className="pw-plan-week-title-button"
                  aria-label={
                    focusMode && !isCurrentWeek
                      ? isFocusCollapsed
                        ? `Expand Week ${week.week || i + 1}`
                        : `Collapse Week ${week.week || i + 1}`
                      : `Open Week ${week.week || i + 1} details`
                  }
                  aria-expanded={focusMode && !isCurrentWeek ? !isFocusCollapsed : undefined}
                  onClick={handleWeekHeaderClick}
                >
                  <h2 className="pw-plan-week-title">
                    <span className="pw-plan-week-title-text">
                      Week {week.week || i + 1}: {week.focus}
                    </span>
                    {focusMode && isCurrentWeek ? (
                      <span className="pw-week-this-week-badge">This week</span>
                    ) : null}
                    {weekComplete ? (
                      <span className="pw-week-complete-badge">
                        Week Complete
                      </span>
                    ) : null}
                  </h2>
                </button>
                {isFocusCollapsed ? (
                  <p className="pw-plan-week-header-summary">
                    {weekDateRange ? (
                      <>
                        <span>{weekDateRange}</span>
                        {" · "}
                      </>
                    ) : null}
                    <span>{totalMileageLabel}</span>
                  </p>
                ) : null}
                {focusMode && !isCurrentWeek ? (
                  <span
                    className={
                      isFocusCollapsed
                        ? "pw-plan-week-chevron"
                        : "pw-plan-week-chevron pw-plan-week-chevron--open"
                    }
                    aria-hidden="true"
                  >
                    ›
                  </span>
                ) : null}
              </div>
              <div className="pw-plan-week-content">
              {raceDayChecked ? (
                <p className="pw-race-day-celebration" role="status">
                  You did it. Every step of training led to this.
                  Congratulations!
                </p>
              ) : null}
              <div
                className={
                  isCalendarView
                    ? "pw-plan-week-body pw-plan-week-body--calendar"
                    : "pw-plan-week-body"
                }
                style={{
                  display: "flex",
                  flexDirection: isCalendarView ? "column" : "row",
                  alignItems: "flex-start",
                  gap: isCalendarView ? "1.5rem" : "1.75rem",
                }}
              >
                <div
                  className="pw-plan-week-main-wrap"
                  style={{
                    flex: isCalendarView ? "1 1 auto" : "0 0 auto",
                    width: isCalendarView
                      ? "100%"
                      : coachingNotesVisible && coaching
                        ? "45%"
                        : "100%",
                    minWidth: 0,
                    alignSelf: "stretch",
                  }}
                >
                  <div className="pw-plan-week-main">
                    <p className="pw-plan-week-mileage">
                      {weekDateRange ? (
                        <>
                          <span className="pw-plan-week-date-range">
                            {weekDateRange}
                          </span>
                          {" · "}
                        </>
                      ) : null}
                      <strong>Total Mileage:</strong>{" "}
                      {displayWeek?.totalMileageLabel ??
                        `${displayWeek?.totalMileage ?? 0} ${
                          unitPref === "Kilometers" ? "km" : "mi"
                        }`}
                    </p>
                    {showWeekCompleteMessage ? (
                      <p className="pw-week-complete-message" role="status">
                        {weekCompleteMessageText}
                      </p>
                    ) : null}

                    <PlanWeekCalendarGrid
                      schedule={week.schedule}
                      intensity={effectiveFormData?.intensity}
                      raceDistance={effectiveFormData?.raceDistance}
                      weekNum={week.week ?? i + 1}
                      workoutChecks={workoutChecks}
                      onToggleWorkoutCheck={toggleWorkoutCheck}
                    />
                  </div>
                </div>

                {coachingNotesVisible && coaching && (
                  <aside
                    className={
                      isCalendarView
                        ? "pw-plan-week-coaching pw-plan-week-coaching--below-calendar"
                        : "pw-plan-week-coaching"
                    }
                    aria-label="Weekly coaching notes"
                    aria-expanded={
                      hasMoreCoaching ? coachingExpanded : undefined
                    }
                    style={{
                      flex: isCalendarView ? "0 0 auto" : 1,
                      alignSelf: "stretch",
                      width: isCalendarView ? "100%" : undefined,
                      minWidth: 0,
                    }}
                  >
                    <h3 className="pw-plan-coaching-title">{coaching.title}</h3>
                    <div className="pw-plan-coaching-body">
                      {bodyParas.map((para, pi) => (
                        <p
                          key={pi}
                          className={
                            showCollapsedPreview && pi > 0
                              ? "pw-coaching-hidden-when-collapsed"
                              : undefined
                          }
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                    {showCollapsedPreview && (
                      <button
                        type="button"
                        className="pw-coaching-toggle"
                        aria-expanded="false"
                        aria-controls={
                          coaching.guidance.length > 0 ||
                          coaching.raceDay ||
                          coaching.recovery
                            ? `pw-coaching-extra-${i}`
                            : undefined
                        }
                        onClick={() => setCoachingWeekOpen(true)}
                      >
                        Show more notes
                      </button>
                    )}
                    {(coaching.guidance.length > 0 ||
                      coaching.raceDay ||
                      coaching.recovery) && (
                      <div
                        id={`pw-coaching-extra-${i}`}
                        className={
                          showCollapsedPreview
                            ? "pw-coaching-hidden-when-collapsed"
                            : undefined
                        }
                      >
                        {coaching.guidance.length > 0 && (
                          <div className="pw-plan-coaching-section">
                            <h4 className="pw-plan-coaching-heading">
                              Workout Guidance
                            </h4>
                            <div className="pw-plan-coaching-guidance-items">
                              {coaching.guidance.map((row, gi) => {
                                const { label, description } =
                                  splitGuidanceLine(row.text);
                                return (
                                  <div
                                    key={gi}
                                    className="pw-plan-coaching-guidance-item"
                                  >
                                    {label ? (
                                      <>
                                        <span className="pw-plan-coaching-guidance-label">
                                          {label}
                                        </span>
                                        <p className="pw-plan-coaching-guidance-desc">
                                          {description}
                                        </p>
                                      </>
                                    ) : (
                                      <p className="pw-plan-coaching-guidance-desc">
                                        {description}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {coaching.raceDay && (
                          <div className="pw-plan-coaching-section">
                            <h4 className="pw-plan-coaching-heading">
                              Race Day Tips
                            </h4>
                            <p className="pw-plan-coaching-recovery">
                              {coaching.raceDay}
                            </p>
                          </div>
                        )}
                        {coaching.recovery && (
                          <div className="pw-plan-coaching-section">
                            <h4 className="pw-plan-coaching-heading">
                              Recovery Tips
                            </h4>
                            <p className="pw-plan-coaching-recovery">
                              {coaching.recovery}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {coachingExpanded && hasMoreCoaching && (
                      <button
                        type="button"
                        className="pw-coaching-toggle pw-coaching-toggle--after"
                        aria-expanded="true"
                        aria-controls={
                          coaching.guidance.length > 0 ||
                          coaching.raceDay ||
                          coaching.recovery
                            ? `pw-coaching-extra-${i}`
                            : undefined
                        }
                        onClick={() => setCoachingWeekOpen(false)}
                      >
                        Show less notes
                      </button>
                    )}
                  </aside>
                )}
              </div>
              </div>
            </motion.div>
          );
        })
        )}
        </div>
      ) : (
        <p>No training plan generated.</p>
      )}

      {isReady && (
        <section
          ref={emailCaptureRef}
          className="pw-plan-email-capture"
          data-pw-exclude-pdf
          aria-labelledby="pw-plan-email-heading"
        >
          <h2 id="pw-plan-email-heading" className="pw-plan-email-title">
            Get your plan in your inbox
          </h2>
          <p className="pw-plan-email-subtext">
            Enter your email and we&apos;ll send you a copy of your plan summary.
          </p>
          {emailSendState === "success" ? (
            <p className="pw-plan-email-success" role="status">
              {emailSuccessMessage}
            </p>
          ) : (
            <>
              <form
                className="pw-plan-email-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendPlanEmail();
                }}
              >
                <input
                  type="email"
                  className="pw-plan-email-input"
                  placeholder="you@example.com"
                  value={emailCapture}
                  onChange={(e) => {
                    setEmailCapture(e.target.value);
                    if (emailSendState === "error") {
                      setEmailSendState("idle");
                      setEmailError("");
                    }
                  }}
                  disabled={emailSendState === "loading"}
                  autoComplete="email"
                  aria-label="Email address"
                />
                <button
                  type="submit"
                  className="pw-plan-email-send-btn"
                  disabled={
                    emailSendState === "loading" || !emailCapture.trim()
                  }
                >
                  {emailSendState === "loading" ? (
                    <>
                      <span className="pw-email-spinner" aria-hidden="true" />
                      Preparing your plan...
                    </>
                  ) : (
                    "Send"
                  )}
                </button>
              </form>
              {emailSendState === "loading" && emailLoadingHint ? (
                <p className="pw-plan-email-loading" role="status">
                  {emailLoadingHint}
                </p>
              ) : null}
            </>
          )}
          {emailSendState === "error" && emailError ? (
            <p className="pw-plan-email-error" role="alert">
              {emailError}
            </p>
          ) : null}
        </section>
      )}
      </div>
      {expandedWeek ? (
        <WeekExpandModal
          week={expandedWeek.week}
          weekIndex={expandedWeek.weekIndex}
          weekDateRange={expandedWeek.weekDateRange}
          totalMileageLabel={expandedWeek.totalMileageLabel}
          intensity={effectiveFormData?.intensity}
          raceDistance={effectiveFormData?.raceDistance}
          workoutChecks={workoutChecks}
          onToggleWorkoutCheck={toggleWorkoutCheck}
          onClose={closeWeekModal}
          closing={weekModalClosing}
          highlightDay={expandedWeek.highlightDay}
        />
      ) : null}
    </div>
  );
}
